/**
 * Client-side website-screenshot store for the viewer-data block lab.
 *
 * A DESIGN PROTOTYPE (debug-only): given a block's source URL we build an
 * Urlbox "render link" — a signed `<img>`-embeddable URL that returns a PNG
 * screenshot of the page (https://urlbox.com/docs/render-links). The resulting
 * link is kept in localStorage keyed by block id, so a tile can swap its card
 * for the live screenshot on hover. Nothing leaves the device except the image
 * request the browser makes when it renders the link.
 *
 * ⚠️  PROTOTYPE-ONLY: the Urlbox secret key is embedded here so the signed
 *     token can be generated in the browser. That leaks the secret to anyone
 *     who loads this experimental screen — fine for a throwaway debug jig, but
 *     this must NOT ship to real users. Move token generation server-side and
 *     rotate the key before this leaves the lab.
 */
import { useCallback, useEffect, useState } from "react";
// --- Urlbox project credentials (prototype) --------------------------------
const URLBOX_PUBLISHABLE_KEY = "ubx_zrTeQ9y1QAVWsC21";
const URLBOX_SECRET_KEY = "ubx_sk_ZDGjoKmaUCnFO5Bw6cnryu3GXhSlvY3L";
const URLBOX_BASE_URL = "https://api.urlbox.com/v1";
// --- Render options (bounded — see security-limits) ------------------------
/**
 * Square capture, in CSS pixels: the viewport is `SHOT_SIZE` × `SHOT_SIZE`, and
 * a viewport (non-full-page) screenshot is exactly that, so the output PNG is a
 * clean 800×800 with no cropping.
 */
const SHOT_SIZE = 800;
/** Never sign/queue more than this many links in a single scrape. */
const MAX_SCRAPE_TARGETS = 200;
/** Give up warming a single render after this long. */
const PRELOAD_TIMEOUT_MS = 45_000;
/** How many screenshots to warm in parallel (gentle on the API). */
const PRELOAD_CONCURRENCY = 4;
// --- Signed render-link construction ---------------------------------------
/**
 * Build the option query string for a target URL. The exact string returned
 * here is BOTH signed and placed after `?` in the render link — Urlbox verifies
 * the token against the raw query string it receives, so the two must match
 * byte-for-byte (option order is therefore fixed).
 */
function buildRenderQuery(targetUrl) {
    const options = [
        ["url", targetUrl],
        ["width", String(SHOT_SIZE)],
        ["height", String(SHOT_SIZE)],
    ];
    return options.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
}
/** HMAC-SHA256 of `query` signed by the secret key, as a lowercase hex digest. */
async function signQuery(query) {
    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey("raw", encoder.encode(URLBOX_SECRET_KEY), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
    const signature = await crypto.subtle.sign("HMAC", key, encoder.encode(query));
    return [...new Uint8Array(signature)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
/**
 * A signed Urlbox render link for `targetUrl`, embeddable directly as an
 * `<img src>`. Shape: `/v1/{publishableKey}/{token}/png?{options}`.
 */
export async function buildScreenshotUrl(targetUrl) {
    const query = buildRenderQuery(targetUrl);
    const token = await signQuery(query);
    return `${URLBOX_BASE_URL}/${URLBOX_PUBLISHABLE_KEY}/${token}/png?${query}`;
}
// --- localStorage store (blockId → render link) ----------------------------
const SHOTS_KEY = "vlab:urlbox-shots";
const SHOW_DEFAULT_KEY = "vlab:urlbox-show-default";
function loadShotMap() {
    try {
        const raw = localStorage.getItem(SHOTS_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null)
            return {};
        return parsed;
    }
    catch {
        return {};
    }
}
function loadShowDefault() {
    try {
        return localStorage.getItem(SHOW_DEFAULT_KEY) === "1";
    }
    catch {
        return false;
    }
}
const listeners = new Set();
function subscribe(fn) {
    listeners.add(fn);
    return () => {
        listeners.delete(fn);
    };
}
function notify() {
    // Defer out of the current render/commit: writers may run inside a state
    // updater, and notifying synchronously would setState mid-render.
    const fns = [...listeners];
    queueMicrotask(() => {
        for (const fn of fns)
            fn();
    });
}
function saveShotMap(map) {
    try {
        localStorage.setItem(SHOTS_KEY, JSON.stringify(map));
    }
    catch {
        // Quota exceeded / storage disabled — prototype, so silently drop.
    }
    notify();
}
function saveShowDefault(on) {
    try {
        localStorage.setItem(SHOW_DEFAULT_KEY, on ? "1" : "0");
    }
    catch {
        // ignore
    }
    notify();
}
// --- Reactive hooks --------------------------------------------------------
/** Reactive render link for one block, or undefined if it hasn't been scraped. */
export function useBlockShot(blockId) {
    const [shot, setShot] = useState(() => loadShotMap()[blockId]);
    useEffect(() => {
        const read = () => setShot(loadShotMap()[blockId]);
        read();
        return subscribe(read);
    }, [blockId]);
    return shot;
}
/** Reactive count of stored screenshots, for the debug panel readout. */
export function useShotCount() {
    const [count, setCount] = useState(() => Object.keys(loadShotMap()).length);
    useEffect(() => {
        const read = () => setCount(Object.keys(loadShotMap()).length);
        read();
        return subscribe(read);
    }, []);
    return count;
}
/** Reactive "show screenshot instead of the card by default" preference. */
export function useShowShotsByDefault() {
    const [value, setValue] = useState(loadShowDefault);
    useEffect(() => {
        const read = () => setValue(loadShowDefault());
        read();
        return subscribe(read);
    }, []);
    const set = useCallback((on) => saveShowDefault(on), []);
    return [value, set];
}
// --- Full-page capture (block "Full Screenshot" view) ----------------------
/** Desktop viewport width for full-page captures; height is content-driven. */
const FULLPAGE_WIDTH = 1280;
/**
 * Option query for a FULL-PAGE render link. `full_page=true` captures the whole
 * scrollable page; `hide_cookie_banners` + `click_accept` keep cookie overlays
 * out of the shot (both recommended by Urlbox for full-page captures —
 * https://urlbox.com/docs/options#full_page). Option order is fixed because the
 * string is signed AND placed after `?`, so the two must match byte-for-byte.
 */
function buildFullPageRenderQuery(targetUrl) {
    const options = [
        ["url", targetUrl],
        ["width", String(FULLPAGE_WIDTH)],
        ["full_page", "true"],
        ["hide_cookie_banners", "true"],
        ["click_accept", "true"],
    ];
    return options.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
}
/** A signed full-page Urlbox render link for `targetUrl`, embeddable as `<img src>`. */
export async function buildFullPageScreenshotUrl(targetUrl) {
    const query = buildFullPageRenderQuery(targetUrl);
    const token = await signQuery(query);
    return `${URLBOX_BASE_URL}/${URLBOX_PUBLISHABLE_KEY}/${token}/png?${query}`;
}
// Full-page shots are stored to the block (localStorage, keyed by block id) just
// like the square hover shots, so a captured page is remembered across reloads.
const FULLPAGE_KEY = "vlab:urlbox-fullpage";
function loadFullPageMap() {
    try {
        const raw = localStorage.getItem(FULLPAGE_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null)
            return {};
        return parsed;
    }
    catch {
        return {};
    }
}
function saveFullPageMap(map) {
    try {
        localStorage.setItem(FULLPAGE_KEY, JSON.stringify(map));
    }
    catch {
        // Quota exceeded / storage disabled — prototype, so silently drop.
    }
    notify();
}
/** Reactive full-page render link stored for one block, or undefined. */
export function useFullPageShot(blockId) {
    const [shot, setShot] = useState(() => loadFullPageMap()[blockId]);
    useEffect(() => {
        const read = () => setShot(loadFullPageMap()[blockId]);
        read();
        return subscribe(read);
    }, [blockId]);
    return shot;
}
/**
 * Build + persist a full-page render link for `blockId` (the screenshot itself
 * is rendered by Urlbox on first `<img>` load). Returns the stored link.
 */
export async function captureFullPageShot(blockId, targetUrl) {
    const renderUrl = await buildFullPageScreenshotUrl(targetUrl);
    const map = loadFullPageMap();
    map[blockId] = renderUrl;
    saveFullPageMap(map);
    return renderUrl;
}
/** Load a render link in a detached <img> to warm the cache; resolves ok/failed. */
function preloadImage(src, timeoutMs) {
    return new Promise((resolve) => {
        const img = new Image();
        let settled = false;
        const finish = (ok) => {
            if (settled)
                return;
            settled = true;
            window.clearTimeout(timer);
            resolve(ok);
        };
        const timer = window.setTimeout(() => finish(false), timeoutMs);
        img.onload = () => finish(true);
        img.onerror = () => finish(false);
        img.src = src;
    });
}
/**
 * Sign + persist a render link for every target (so tiles can show them right
 * away), then warm each render in the background reporting progress. Targets
 * are deduped by block id and capped at `MAX_SCRAPE_TARGETS`.
 */
export async function scrapeScreenshots(targets, onProgress) {
    const seen = new Set();
    const list = [];
    for (const t of targets) {
        if (!t.url || seen.has(t.blockId))
            continue;
        seen.add(t.blockId);
        list.push(t);
        if (list.length >= MAX_SCRAPE_TARGETS)
            break;
    }
    if (list.length === 0)
        return { requested: 0, succeeded: 0, failed: 0 };
    // Phase 1 — sign + persist all links up front so tiles light up immediately.
    const map = loadShotMap();
    const built = [];
    for (const t of list) {
        try {
            const renderUrl = await buildScreenshotUrl(t.url);
            map[t.blockId] = renderUrl;
            built.push(renderUrl);
        }
        catch (err) {
            console.warn("[urlbox] failed to sign render link for", t.url, err);
        }
    }
    saveShotMap(map);
    // Phase 2 — warm the renders (gentle concurrency) so hover is instant, and
    // surface how many actually came back.
    let done = 0;
    let succeeded = 0;
    let failed = 0;
    let cursor = 0;
    const worker = async () => {
        for (;;) {
            const src = built[cursor];
            cursor += 1;
            if (src === undefined)
                return;
            const ok = await preloadImage(src, PRELOAD_TIMEOUT_MS);
            if (ok === false) {
                failed += 1;
                console.warn("[urlbox] failed to load render", src);
            }
            else {
                succeeded += 1;
            }
            done += 1;
            onProgress?.({ done, total: built.length });
        }
    };
    await Promise.all(Array.from({ length: Math.min(PRELOAD_CONCURRENCY, built.length) }, () => worker()));
    return { requested: built.length, succeeded, failed };
}
